import { type LoginRequest, loginRequestSchema } from "@eduleo/shared";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { Button } from "../../components/ui/button";
import { Alert } from "../../components/ui/feedback";
import { PasswordField } from "../../components/ui/password-field";
import { TextField } from "../../components/ui/text-field";
import { ApiError, describeError } from "../../lib/api-client";
import { landingFor } from "../../lib/sign-in-paths";
import { useAuthStore } from "../../stores/auth-store";
import { usePreferencesStore } from "../../stores/preferences-store";
import { login } from "./auth-api";
import { CHANGE_PASSWORD_PATH } from "./require-auth";
import { SignInHeroBanner, SignInHeroPanel } from "./sign-in-hero";

type FieldName = keyof LoginRequest;
const FIELD_ORDER: FieldName[] = ["institutionId", "userId", "password"];

export function LoginPage() {
  const status = useAuthStore((state) => state.status);
  const signedInUser = useAuthStore((state) => state.user);
  const setSession = useAuthStore((state) => state.setSession);
  const { rememberedInstitutionId, rememberInstitution, setRememberedInstitution } =
    usePreferencesStore();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [values, setValues] = useState<LoginRequest>({
    institutionId: rememberedInstitutionId,
    userId: "",
    password: "",
  });
  const [remember, setRemember] = useState(rememberInstitution);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldName, string>>>({});
  const inputs = useRef<Partial<Record<FieldName, HTMLInputElement | null>>>({});

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (session) => {
      setRememberedInstitution(session.user.institution.code, remember);
      setSession(session);
      // A password someone else set (new account, reset) must be replaced before anything else.
      if (session.user.mustChangePassword) {
        navigate(CHANGE_PASSWORD_PATH, {
          replace: true,
          state: { from: landingFor(session.user, from), forced: true },
        });
      } else {
        navigate(landingFor(session.user, from), { replace: true });
      }
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "VALIDATION_FAILED")
        showFieldErrors(error.fieldErrors);
      if (error instanceof ApiError && error.code === "INVALID_CREDENTIALS") {
        setValues((current) => ({ ...current, password: "" }));
        inputs.current.password?.focus();
      }
    },
  });

  if (status === "signed_in") return <Navigate to={landingFor(signedInUser, from)} replace />;

  function showFieldErrors(errors: { path: string; message: string }[]) {
    const next: Partial<Record<FieldName, string>> = {};
    for (const error of errors) {
      const field = error.path as FieldName;
      if (FIELD_ORDER.includes(field) && !next[field]) next[field] = error.message;
    }
    setFieldErrors(next);
    const first = FIELD_ORDER.find((field) => next[field]);
    if (first) inputs.current[first]?.focus();
  }

  function update(field: FieldName, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
    if (fieldErrors[field]) setFieldErrors((current) => ({ ...current, [field]: undefined }));
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mutation.isPending) return;
    const parsed = loginRequestSchema.safeParse(values);
    if (!parsed.success) {
      showFieldErrors(
        parsed.error.issues.map((issue) => ({
          path: String(issue.path[0]),
          message: issue.message,
        })),
      );
      return;
    }
    setFieldErrors({});
    mutation.mutate(parsed.data);
  }

  const serverError =
    mutation.error &&
    !(mutation.error instanceof ApiError && mutation.error.code === "VALIDATION_FAILED")
      ? describeError(mutation.error)
      : null;

  return (
    <div className="min-h-dvh bg-canvas lg:grid lg:grid-cols-[1.1fr_1fr]">
      <SignInHeroPanel />

      <main className="flex min-h-dvh flex-col items-center justify-center px-4 py-6 sm:px-6 sm:py-10">
        <div className="w-full max-w-[420px]">
          <SignInHeroBanner />

          <div className="rounded-xl border border-edge bg-surface p-6 shadow-sm sm:p-8">
            <h2 className="font-bold text-2xl">Sign in</h2>
            <p className="mt-1 text-ink-2">Use the details your school gave you.</p>

            {serverError && (
              <Alert tone="danger" title={serverError.title} className="mt-5">
                {serverError.detail}
              </Alert>
            )}

            <form
              noValidate
              onSubmit={onSubmit}
              className="mt-6 flex flex-col gap-5"
              aria-label="Sign in"
            >
              <TextField
                ref={(element) => {
                  inputs.current.institutionId = element;
                }}
                label="Institution ID"
                name="institutionId"
                value={values.institutionId}
                onChange={(event) => update("institutionId", event.target.value)}
                error={fieldErrors.institutionId}
                hint="Your school's code, printed on your ID card. For example DPS-RKP."
                autoComplete="organization"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                className="uppercase placeholder:normal-case"
                placeholder="e.g. DPS-RKP"
                autoFocus={!values.institutionId}
              />
              <TextField
                ref={(element) => {
                  inputs.current.userId = element;
                }}
                label="User ID"
                name="userId"
                value={values.userId}
                onChange={(event) => update("userId", event.target.value)}
                error={fieldErrors.userId}
                hint="6 to 8 digits, from your school office."
                placeholder="e.g. 482057"
                // A text field with the number pad: User IDs are digits, but not a quantity.
                inputMode="numeric"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className="font-mono"
                autoFocus={Boolean(values.institutionId)}
              />
              <PasswordField
                ref={(element) => {
                  inputs.current.password = element;
                }}
                label="Password"
                name="password"
                value={values.password}
                onChange={(event) => update("password", event.target.value)}
                error={fieldErrors.password}
                autoComplete="current-password"
              />

              <label className="flex min-h-11 cursor-pointer items-center gap-3 text-ink-2 md:min-h-0">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(event) => setRemember(event.target.checked)}
                  className="size-[18px] cursor-pointer accent-brand"
                />
                Remember Institution ID on this device
              </label>

              <Button
                type="submit"
                size="lg"
                block
                loading={mutation.isPending}
                loadingText="Signing in…"
              >
                Sign in
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-ink-2 text-sm">
            Forgot your password? Ask your school office to reset it.
          </p>
        </div>
      </main>
    </div>
  );
}
